import { captureException } from './lib/sentry';

const BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

function token() { return localStorage.getItem('rivet_token') || ''; }
function setToken(t) { if (t) localStorage.setItem('rivet_token', t); else localStorage.removeItem('rivet_token'); }

async function request(path, opts = {}) {
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  const t = token();
  if (t) headers.Authorization = `Bearer ${t}`;
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers,
    body: opts.body && typeof opts.body !== 'string' ? JSON.stringify(opts.body) : opts.body,
  });
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');
  const data = isJson ? await res.json() : await res.text();
  if (!res.ok) {
    // Auto-logout on 401: token is invalid/expired. Skip on the login path itself
    // so a wrong-password attempt surfaces an error inline instead of redirecting.
    if (res.status === 401 && t && !path.startsWith('/v1/auth/login') && !path.startsWith('/v1/auth/signup')) {
      setToken('');
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}&reason=expired`);
        // Return a never-resolving promise — the page is navigating away, so we don't
        // want callers' .catch handlers to fire and render a toast before the redirect.
        return new Promise(() => {});
      }
    }
    const err = new Error((data && data.error && data.error.message) || res.statusText);
    err.code = data && data.error && data.error.code;
    err.status = res.status;
    err.data = data;
    // Only report server-side bugs (5xx) to Sentry. 4xx are user errors —
    // bad input, auth, not-found — and shouldn't pollute the bug tracker.
    // The session-expired path above already returned a never-resolving
    // promise so it never reaches here.
    if (res.status >= 500) captureException(err);
    throw err;
  }
  return data;
}

export const api = {
  base: BASE,
  token,
  setToken,
  get: (p) => request(p),
  post: (p, body) => request(p, { method: 'POST', body }),
  patch: (p, body) => request(p, { method: 'PATCH', body }),
  del: (p) => request(p, { method: 'DELETE' }),
  login: (email, password) => request('/v1/auth/login', { method: 'POST', body: { email, password } }),
  signup: (body) => request('/v1/auth/signup', { method: 'POST', body }),
  // Email verification (Cognito-backed). signup may return { pendingVerification, email }
  // when verification is enabled; verifyEmail returns the session { token, user, tenant }.
  verifyEmail: (email, code) => request('/v1/auth/verify', { method: 'POST', body: { email, code } }),
  resendCode: (email) => request('/v1/auth/verify/resend', { method: 'POST', body: { email } }),
  me: () => request('/v1/auth/me'),
  // mailbox
  listInbound: (params = {}) => {
    if (typeof params === 'string') params = { status: params };
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return request('/v1/inbound-emails' + (qs ? `?${qs}` : ''));
  },
  // Lazy-load body for a single email (cache → provider → unavailable)
  getInboundBody: (id) => request(`/v1/inbound-emails/${id}/body`),
  patchInbound: (id, body) => request(`/v1/inbound-emails/${id}`, { method: 'PATCH', body }),
  convertInbound: (id, body) => request(`/v1/inbound-emails/${id}/convert`, { method: 'POST', body }),
  // Tenant settings (data retention)
  getTenantSettings: () => request('/v1/tenant/settings'),
  updateTenantSettings: (body) => request('/v1/tenant/settings', { method: 'PATCH', body }),
  runEmailBodyBackfill: () => request('/v1/tenant/email-body-backfill', { method: 'POST' }),
  // rfq
  listRFQs: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return request('/v1/rfqs' + (qs ? `?${qs}` : ''));
  },
  createRFQ: (body) => request('/v1/rfqs', { method: 'POST', body }),
  getRFQ: (id) => request(`/v1/rfqs/${id}`),
  patchRFQ: (id, body) => request(`/v1/rfqs/${id}`, { method: 'PATCH', body }),
  addLines: (id, lines) => request(`/v1/rfqs/${id}/lines`, { method: 'POST', body: lines }),
  updateLine: (id, lineId, patch) => request(`/v1/rfqs/${id}/lines/${lineId}`, { method: 'PATCH', body: patch }),
  deleteLine: (id, lineId) => request(`/v1/rfqs/${id}/lines/${lineId}`, { method: 'DELETE' }),
  // suppliers / customers
  listSuppliers: () => request('/v1/suppliers'),
  createSupplier: (body) => request('/v1/suppliers', { method: 'POST', body }),
  updateSupplier: (id, body) => request(`/v1/suppliers/${id}`, { method: 'PATCH', body }),
  listCustomers: () => request('/v1/customers'),
  createCustomer: (body) => request('/v1/customers', { method: 'POST', body }),
  updateCustomer: (id, body) => request(`/v1/customers/${id}`, { method: 'PATCH', body }),
  // dispatch + quote
  dispatch: (id, supplierIds, lineItemIds, method, connectorId, ccBySupplier, opts = {}) => {
    const body = { supplierIds };
    if (lineItemIds && lineItemIds.length > 0) body.lineItemIds = lineItemIds;
    if (method) body.method = method;
    if (connectorId) body.connectorId = connectorId;
    if (ccBySupplier && Object.keys(ccBySupplier).length > 0) body.ccBySupplier = ccBySupplier;
    // Optional user-edited email text — sent verbatim to all suppliers when set.
    if (opts.customSubject && opts.customSubject.trim()) body.customSubject = opts.customSubject;
    if (opts.customBody && opts.customBody.trim()) body.customBody = opts.customBody;
    return request(`/v1/rfqs/${id}/dispatch`, { method: 'POST', body });
  },
  // TODO: Phase G backend must implement PATCH /v1/me/preferences
  updatePreferences: (body) => request('/v1/me/preferences', { method: 'PATCH', body }).catch(() => null),
  // TODO: Phase A backend must implement GET /v1/rfqs/:id/dispatch-batches
  listDispatchBatches: (id) => request(`/v1/rfqs/${id}/dispatch-batches`).catch(() => ({ items: [] })),
  // TODO: Phase A backend must implement GET /v1/rfqs/:id/lines/:lineId/envelopes
  getEnvelopesForLine: (rfqId, lineId) => request(`/v1/rfqs/${rfqId}/lines/${lineId}/envelopes`).catch(() => ({ items: [] })),
  listEnvelopes: (id) => request(`/v1/rfqs/${id}/supplier-quotes`),
  patchEnvelope: (id, body) => request(`/v1/supplier-quotes/${id}`, { method: 'PATCH', body }),
  listQuoteLines: (envId) => request(`/v1/supplier-quotes/${envId}/lines`),
  createQuoteLine: (envId, body) => request(`/v1/supplier-quotes/${envId}/lines`, { method: 'POST', body }),
  patchQuoteLine: (id, body) => request(`/v1/quote-lines/${id}`, { method: 'PATCH', body }),
  // compare + decide + quote
  comparison: (id, lineItemIds) => {
    const qs = lineItemIds && lineItemIds.length > 0 ? `?lineItemIds=${lineItemIds.join(',')}` : '';
    return request(`/v1/rfqs/${id}/comparison${qs}`);
  },
  commitDecision: (id, body) => request(`/v1/rfqs/${id}/decisions`, { method: 'POST', body }),
  listDecisions: (id) => request(`/v1/rfqs/${id}/decisions`),
  // Feature B — decision revisions.
  listCurrentDecisions: (id) => request(`/v1/rfqs/${id}/decisions?currentOnly=true`),
  decisionRevisions: (id) => request(`/v1/rfqs/${id}/decision-revisions`),
  revisionPreflight: (id, decisionId) =>
    request(`/v1/rfqs/${id}/revision-preflight${decisionId ? `?decisionId=${decisionId}` : ''}`),
  audit: (decisionId) => request(`/v1/decisions/${decisionId}/audit`),
  // Order confirmations
  awardsBySupplier: (rfqId) => request(`/v1/rfqs/${rfqId}/awards-by-supplier`),
  sendOrderConfirmation: (rfqId, body) => request(`/v1/rfqs/${rfqId}/order-confirmations`, { method: 'POST', body: body || {} }),
  createCustomerQuote: (id, body) => request(`/v1/rfqs/${id}/customer-quotes`, { method: 'POST', body }),
  getCustomerQuote: (id) => request(`/v1/customer-quotes/${id}`),
  exportUrl: (id, format = 'csv') => `${BASE}/v1/customer-quotes/${id}/export?format=${format}`,
  auditExportUrl: (id, format = 'json') => `${BASE}/v1/decisions/${id}/audit.${format}`,
  // Authenticated download: GET with Bearer header → blob → trigger browser save.
  // Use this for export endpoints because plain <a href> can't carry the JWT.
  download: async (url, filename) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token()}` } });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`Download failed (${res.status}): ${text.slice(0, 200) || res.statusText}`);
    }
    const blob = await res.blob();
    // For HTML/PDF the user wants to view-and-print, not download — open in a new tab
    const isHtml = (res.headers.get('content-type') || '').includes('text/html');
    const objUrl = URL.createObjectURL(blob);
    if (isHtml) {
      window.open(objUrl, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  },
  templateUrl: () => `${BASE}/templates/rfq-template.xlsx`,
  search: (q) => request(`/v1/search?q=${encodeURIComponent(q)}`),
  uploadRfqFile: async (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/v1/rfqs/${id}/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token()}` },
      body: fd,
    });
    if (!res.ok) throw new Error((await res.json()).error?.message || res.statusText);
    return res.json();
  },
  // Supplier quote sheet import (#11) — parse/preview, then commit.
  uploadQuoteSheet: async (envelopeId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/v1/supplier-quotes/${envelopeId}/upload-quote`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token()}` },
      body: fd,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error?.message || res.statusText);
    }
    return res.json();
  },
  commitQuoteSheet: (envelopeId, lines) =>
    request(`/v1/supplier-quotes/${envelopeId}/commit-upload`, { method: 'POST', body: { lines } }),
  // logistics
  getLogistics: (rfqId) => request(`/v1/rfqs/${rfqId}/logistics`),
  patchLogistics: (rfqId, body) => request(`/v1/rfqs/${rfqId}/logistics`, { method: 'PATCH', body }),
  // rfq export
  rfqExportUrl: (rfqId, format = 'json') => `${BASE}/v1/rfqs/${rfqId}/export?format=${format}`,
  // dashboard
  kpis: () => request('/v1/dashboard/kpis'),
  supplierBehavior: (id) => request(`/v1/suppliers/${id}/behavior`),
  supplierBehaviorExportUrl: (id) => `${BASE}/v1/suppliers/${id}/behavior/export?format=csv`,
  // users / invites / auth extras
  listUsers: () => request('/v1/users'),
  inviteUser: (body) => request('/v1/users/invite', { method: 'POST', body }),
  patchUser: (id, body) => request(`/v1/users/${id}`, { method: 'PATCH', body }),
  acceptInvite: (token2, password) => request('/v1/auth/accept-invite', { method: 'POST', body: { token: token2, password } }),
  forgot: (email) => request('/v1/auth/forgot', { method: 'POST', body: { email } }),
  reset: (token2, password) => request('/v1/auth/reset', { method: 'POST', body: { token: token2, password } }),
  getTenant: () => request('/v1/tenant'),
  patchTenant: (body) => request('/v1/tenant', { method: 'PATCH', body }),
  // onboarding + notifications
  seedDemo: () => request('/v1/onboarding/seed', { method: 'POST' }),
  notifications: () => request('/v1/notifications'),
  // mailbox connectors (multi-mailbox)
  listConnectors: () => request('/v1/mailbox/connectors'),
  configureImap: (body) => request('/v1/mailbox/connectors/imap', { method: 'POST', body }),
  testImap: (body) => request('/v1/mailbox/connectors/test', { method: 'POST', body }),
  syncConnector: (id) => request(`/v1/mailbox/connectors/${id}/sync`, { method: 'POST' }),
  searchConnector: (id, body) => request(`/v1/mailbox/connectors/${id}/search`, { method: 'POST', body }),
  patchConnector: (id, body) => request(`/v1/mailbox/connectors/${id}`, { method: 'PATCH', body }),
  deleteConnector: (id, hard = false) => request(`/v1/mailbox/connectors/${id}${hard ? '?hard=1' : ''}`, { method: 'DELETE' }),
  oauthStart: (provider) => request(`/v1/oauth/${provider}/start`),
  // extracted-preview (auto-extracted supplier replies via Bedrock)
  listExtractedPreviews: () => request('/v1/extracted-previews'),
  commitExtractedPreview: (emailId, body = {}) => request(`/v1/extracted-previews/${emailId}/commit`, { method: 'POST', body }),
  // Phase D2 — Email threading + manual link
  linkEmail: (emailId, body) => request(`/v1/inbound-emails/${emailId}/link`, { method: 'POST', body }),
  unlinkEmail: (emailId) => request(`/v1/inbound-emails/${emailId}/link`, { method: 'DELETE' }),
  linkThread: (threadId, body) => request(`/v1/threads/${threadId}/link`, { method: 'POST', body }),
  reextractEmail: (emailId, body = {}) => request(`/v1/inbound-emails/${emailId}/extract`, { method: 'POST', body }),
  listThreads: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return request('/v1/threads' + (qs ? `?${qs}` : ''));
  },
  getThread: (threadId) => request(`/v1/threads/${threadId}`),
  // Phase D3 — Quote revisions
  listRevisions: (envelopeId) => request(`/v1/supplier-quotes/${envelopeId}/revisions`),
  getRevision: (envelopeId, revision) => request(`/v1/supplier-quotes/${envelopeId}/lines/at-revision?revision=${revision}`),
  restoreQuoteLine: (quoteLineId) => request(`/v1/quote-lines/${quoteLineId}/restore`, { method: 'POST' }),
  finalizeEnvelope: (envelopeId) => request(`/v1/supplier-quotes/${envelopeId}/finalize`, { method: 'POST' }),
};
